import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import { actors } from '@/mocks/actors';
import { movies } from '@/mocks/movies';

export default function ActorDetail() {
  const { id } = useParams();
  const actor = useMemo(() => actors.find((a) => a.id === id), [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const works = useMemo(
    () => (actor ? movies.filter((m) => actor.workIds.includes(m.id)) : []),
    [actor],
  );

  if (!actor) {
    return (
      <SiteLayout>
        <title>未找到该演员资料 - 蜜桃成熟时33D</title>
        <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-user-unfollow-line text-4xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            未找到该演员资料
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-500">
            该演员资料可能已被移除，你可以返回演员列表浏览其他成员。
          </p>
          <Link
            to="/actors"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回演员列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <title>{`${actor.name}演员资料与代表作品 - 蜜桃成熟时33D`}</title>
      <meta
        name="description"
        content={`${actor.name}的详细个人资料与代表作品介绍，包含基本信息、个人简介以及相关影片链接。`}
      />
      <meta name="keywords" content={`${actor.name},${actor.name}资料,演员简介,代表作品,蜜桃成熟时33D演员`} />
      <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '演员信息', to: '/actors' },
            { label: actor.name },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Photo */}
          <div className="lg:col-span-4">
            <div className="relative mx-auto aspect-square w-full max-w-xs overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src={actor.avatar}
                alt={`${actor.name}照片`}
                title={`${actor.name}演员资料`}
                className="h-full w-full object-top"
              />
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-heading text-3xl font-bold text-foreground-950">
                  {actor.name}
                </h1>
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
                  {actor.roleTag}
                </span>
              </div>
              <p className="mt-4 text-sm leading-7 text-foreground-700">{actor.summary}</p>

              <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  ['出生年份', `${actor.birthYear} 年`],
                  ['地区', actor.region],
                  ['身高', actor.height],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-md bg-background-100 p-4">
                    <dt className="text-xs text-foreground-500">{k}</dt>
                    <dd className="mt-1 text-sm font-semibold text-foreground-900">{v}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">个人简介</h2>
                <div className="mt-3 space-y-3">
                  {actor.bio.map((p, idx) => (
                    <p key={idx} className="text-sm leading-7 text-foreground-700">
                      {p}
                    </p>
                  ))}
                </div>
                <p className="mt-4 text-xs leading-relaxed text-foreground-400">
                  以上个人信息为虚构演示内容，不涉及任何真实人物资料。
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Works */}
        <div className="mt-10">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950">代表作品</h2>
            <Link
              to="/actors"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              <i className="ri-arrow-left-line"></i>
              返回演员列表
            </Link>
          </div>

          {works.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {works.map((m) => (
                <Link
                  key={m.id}
                  to={`/movies/detail/${m.id}`}
                  className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <div className="h-24 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={m.poster}
                      alt={`${m.title}海报`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {m.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">{m.year} · {m.region}</p>
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                      {m.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
              <i className="ri-film-line text-3xl text-foreground-400"></i>
              <p className="mt-3 text-sm text-foreground-500">暂无相关作品记录</p>
            </div>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}